<template>
  <transition name="drawer">
    <aside
      v-if="isOpen"
      class="ai-assistant-drawer"
      :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
    >
      <header class="ai-assistant-header">
        <h3>{{ $t('aiAssistant.title') }}</h3>
        <div class="header-actions">
          <button
            class="text-btn"
            :title="$t('aiAssistant.switchers.openButton')"
            @click="switchersOpen = true"
          >
            {{ $t('aiAssistant.switchers.openButton') }}
          </button>
          <button
            v-if="chat && chat.state.messages.length"
            class="text-btn"
            :title="$t('aiAssistant.reset')"
            @click="chat.reset"
          >
            {{ $t('aiAssistant.reset') }}
          </button>
          <button
            class="close-btn"
            :title="$t('aiAssistant.minimize')"
            :aria-label="$t('aiAssistant.minimize')"
            @click="close"
          >
            &minus;
          </button>
        </div>
      </header>
      <Thread v-if="chat" :chat="chat" />
      <SwitchersFixPanel :open="switchersOpen" @close="switchersOpen = false" />
    </aside>
  </transition>
</template>

<script>
import Thread from "./Thread.vue";
import SwitchersFixPanel from "./SwitchersFixPanel.vue";
import { createChatStream } from "./composables/useChatStream";

export default {
  name: "AiAssistantSidebar",
  components: { Thread, SwitchersFixPanel },
  data() {
    return {
      chat: null,
      switchersOpen: false,
    };
  },
  computed: {
    isOpen() {
      return this.$store.state.aiAssistant?.isOpen ?? false;
    },
  },
  created() {
    const baseUrl = import.meta.env.VITE_KHABEER_AI_URL;
    if (!baseUrl) {
      console.warn("VITE_KHABEER_AI_URL not set; Khabeer AI disabled.");
      return;
    }
    this.chat = createChatStream({
      apiUrl: `${baseUrl}/api/chat`,
      executeUrl: `${baseUrl}/api/execute-tool`,
      getToken: () => this.$store.state.user?.accessToken ?? "",
      onAuthError: () => {
        if (this._authExpiredHandled) return;
        this._authExpiredHandled = true;
        this.$store.commit("aiAssistant/close");
        this.$store.dispatch("removeUser");
        window.location.href = "/login";
      },
      onForbidden: () => {
        // Server-side allowlist rejected this officer — hide the UI. The
        // backend stays the source of truth; this just keeps the bubble
        // from reappearing for the rest of the session.
        this.$store.commit("aiAssistant/setAllowed", false);
      },
    });
  },
  methods: {
    close() {
      this.$store.commit("aiAssistant/close");
    },
  },
};
</script>

<style scoped>
.ai-assistant-drawer {
  position: fixed;
  top: 0;
  inset-inline-end: 0;
  width: 420px;
  max-width: 100vw;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  z-index: 1100;
}
.ai-assistant-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f5f3ff 0%, #eef2ff 100%);
}
.ai-assistant-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #312e81;
}
.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.text-btn {
  background: none;
  border: none;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
}
.text-btn:hover {
  background: rgba(99, 102, 241, 0.1);
  color: #4f46e5;
}
.close-btn {
  background: none;
  border: none;
  font-size: 26px;
  color: #6b7280;
  cursor: pointer;
  padding: 0 6px;
  line-height: 1;
}
.close-btn:hover {
  color: #111;
}
.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.2s ease;
}
.drawer-enter,
.drawer-leave-to {
  transform: translateX(100%);
}
[dir="rtl"] .drawer-enter,
[dir="rtl"] .drawer-leave-to {
  transform: translateX(-100%);
}
</style>
